import React, { useState, useEffect, useCallback } from 'react';
import Sidebar from './components/Sidebar';
import Dashboard from './pages/Dashboard';
import ConfigPage from './pages/ConfigPage';
import GroupsPage from './pages/GroupsPage';
import ChatPage from './pages/ChatPage';
import InstagramLinksPage from './pages/InstagramLinksPage';
import BillingPage from './pages/BillingPage';
import AdminApp from './pages/admin/AdminApp';
import PublicVitrinePage from './pages/PublicVitrinePage';
import { Menu } from 'lucide-react';
import { Toaster } from 'react-hot-toast';
import LoginPage from './pages/LoginPage';
import LandingPage from './pages/LandingPage';
import api from './api';
import NotificationsBell from './components/NotificationsBell';
import ProfileDropdown from './components/ProfileDropdown';
import ProfileModal from './components/ProfileModal';
import CookieConsent from './components/CookieConsent';

function App() {
  const [activeTab, setActiveTab] = useState('home');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // Agora usamos is_logged_in em vez de admin_token. O token real vive num Cookie HttpOnly seguro.
  const [isAuthenticated, setIsAuthenticated] = useState(!!localStorage.getItem('is_logged_in'));
  const [showAuth, setShowAuth] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [pageLoading, setPageLoading] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  const [profileModalTab, setProfileModalTab] = useState(null); // 'data' | 'password' | null

  const userPlan = userProfile?.plan === 'admin' ? 'premium' : (userProfile?.plan || 'free');

  useEffect(() => {
    if (isAuthenticated) {
      setPageLoading(true);
      const timer = setTimeout(() => {
        setPageLoading(false);
      }, 350);
      return () => clearTimeout(timer);
    }
  }, [activeTab, isAuthenticated]);

  const fetchProfile = useCallback(() => {
    if (isAuthenticated) {
      api.get('/auth/me')
        .then(res => setUserProfile(res.data))
        .catch(err => console.error("Erro ao obter perfil:", err));
    } else {
      setUserProfile(null);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    fetchProfile();
  }, [isAuthenticated, activeTab, fetchProfile]);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      if (mobile) {
        setSidebarOpen(false); // Garante que fecha ao redimensionar para mobile
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleLogout = async () => {
    try {
      await api.post('/auth/logout');
    } catch (e) {
      console.error('Logout error', e);
    }
    localStorage.removeItem('is_logged_in');
    localStorage.removeItem('admin_token'); // Limpeza por segurança
    setIsAuthenticated(false);
  };

  // Rota administrativa dedicada (/admin ou /login-admin)
  const path = window.location.pathname;
  if (path.startsWith('/admin') || path.startsWith('/login-admin')) {
    return <AdminApp />;
  }

  // Rota pública para a Vitrine
  if (path.startsWith('/vitrine/')) {
    const vitrineUsername = path.split('/vitrine/')[1];
    return (
      <>
        <PublicVitrinePage username={vitrineUsername} />
        <Toaster position="top-right" />
        <CookieConsent />
      </>
    );
  }

  if (!isAuthenticated) {
    if (showAuth) {
      return (
        <>
          <LoginPage
            onLogin={() => setIsAuthenticated(true)}
            onBackToLanding={() => setShowAuth(false)}
            initialMode={showAuth}
          />
          <Toaster position="top-right" />
          <CookieConsent />
        </>
      );
    }
    return (
      <>
        <LandingPage onNavigateAuth={(mode) => setShowAuth(mode)} />
        <Toaster position="top-right" />
        <CookieConsent />
      </>
    );
  }
  const renderContent = () => {
    switch (activeTab) {
      case 'home': return <Dashboard userPlan={userPlan} userRole={userProfile?.role} onNavigateTab={setActiveTab} />;
      case 'config': return <ConfigPage userPlan={userPlan} userRole={userProfile?.role} userId={userProfile?.id} username={userProfile?.username} onNavigateTab={setActiveTab} />;
      case 'grupos': return <GroupsPage userPlan={userPlan} onNavigateTab={setActiveTab} />;
      case 'chat': return <ChatPage />;
      case 'instagram_links': return <InstagramLinksPage />;
      case 'billing': return <BillingPage />;
      default: return <Dashboard userPlan={userPlan} userRole={userProfile?.role} onNavigateTab={setActiveTab} />;
    }
  };

  const mainStyle = isMobile ? {
    flex: 1,
    padding: '20px 15px',
    marginLeft: '0',
    transition: '0.3s',
    paddingTop: '80px',
    boxSizing: 'border-box',
    width: '100%'
  } : {
    flex: 1,
    padding: '40px',
    marginLeft: sidebarOpen ? '250px' : '0',
    transition: '0.3s',
    paddingTop: sidebarOpen ? '40px' : '80px',
    boxSizing: 'border-box'
  };

  return (
    <div style={{ display: 'flex', minHeight: '100vh', backgroundColor: '#f8fafc', overflowX: 'hidden' }}>

      {/* Backdrop para fechar o menu no mobile */}
      {isMobile && sidebarOpen && (
        <div
          onClick={() => setSidebarOpen(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(15, 23, 42, 0.6)',
            backdropFilter: 'blur(4px)',
            zIndex: 950,
            transition: '0.3s'
          }}
        />
      )}

      {/* Header Fixo do Mobile */}
      {isMobile && (
        <header style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          height: '60px',
          backgroundColor: 'rgba(255, 255, 255, 0.85)',
          backdropFilter: 'blur(12px)',
          borderBottom: '1px solid #e2e8f0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 20px',
          zIndex: 800,
          boxShadow: '0 1px 3px rgba(0,0,0,0.02)'
        }}>
          <button
            onClick={() => setSidebarOpen(true)}
            style={{
              background: 'none',
              border: 'none',
              color: '#334155',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <Menu size={24} />
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <NotificationsBell />
            <ProfileDropdown
              userProfile={userProfile}
              onOpenModal={(tab) => setProfileModalTab(tab)}
              handleLogout={handleLogout}
            />
          </div>
        </header>
      )}

      {/* Hamburguer Flutuante do Desktop */}
      {!isMobile && !sidebarOpen && (
        <button
          onClick={() => setSidebarOpen(true)}
          style={{
            position: 'fixed',
            top: '20px',
            left: '20px',
            zIndex: 900,
            backgroundColor: '#ee4d2d',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            padding: '10px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)'
          }}
        >
          <Menu size={24} />
        </button>
      )}

      {/* Sino de Notificações Desktop e ProfileDropdown */}
      {!isMobile && (
        <div style={{ position: 'fixed', top: '20px', right: '40px', zIndex: 900, display: 'flex', alignItems: 'center', gap: '15px' }}>
          <NotificationsBell />
          <ProfileDropdown
            userProfile={userProfile}
            onOpenModal={(tab) => setProfileModalTab(tab)}
            handleLogout={handleLogout}
          />
        </div>
      )}

      <Sidebar
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
        handleLogout={handleLogout}
        isMobile={isMobile}
        userPlan={userPlan}
        userRole={userProfile?.role}
      />

      <main style={mainStyle}>
        {pageLoading ? (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            height: '60vh',
            animation: 'fadeIn 0.25s ease-out'
          }}>
            <div style={{
              width: '50px',
              height: '50px',
              border: '3px solid #e2e8f0',
              borderTop: '3px solid #ee4d2d',
              borderRadius: '50%',
              animation: 'spin 0.8s linear infinite'
            }} />
            <p style={{ marginTop: '20px', color: '#64748b', fontSize: '14px', fontWeight: '800', letterSpacing: '0.05em' }}>
              Carregando dados...
            </p>
          </div>
        ) : (
          <div style={{ animation: 'fadeIn 0.35s ease-out' }}>
            {renderContent()}
          </div>
        )}
      </main>

      {profileModalTab && (
        <ProfileModal
          userProfile={userProfile}
          initialTab={profileModalTab}
          onClose={() => setProfileModalTab(null)}
          onSuccess={fetchProfile}
        />
      )}

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
      <Toaster
        position="top-right" // Ou "top-left" como você preferir
        reverseOrder={false}
        toastOptions={{
          // Estilo padrão para todos os toasts
          style: {
            borderRadius: '12px',
            background: '#333',
            color: '#fff',
            fontSize: '14px',
            padding: '16px',
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
          },
          // Customização por tipo
          success: {
            duration: 4000,
            iconTheme: {
              primary: '#22c55e',
              secondary: '#fff',
            },
          },
          error: {
            duration: 5000,
            iconTheme: {
              primary: '#ef4444',
              secondary: '#fff',
            },
          },
        }}
      />
      <CookieConsent />
    </div>
  );
}

export default App;

